import { Middleware } from "@reduxjs/toolkit";
import { loginAction, logOutAction } from "redux/authSlice";
import { identifyMixpanel, resetMixpanel } from "utils/mixpanel";

/**
 * Keeps the Mixpanel identity in step with the Redux auth state.
 *
 * Identify/reset hang off `loginAction`/`logOutAction` rather than off the
 * individual screens because there are ~7 ways to log in (password, SSO,
 * Google OAuth, passkey, TOTP, registration, MFA setup) and ~10 ways to log
 * out (explicit, idle timeout, 401 expiry, account closure, password expiry).
 * Only these two actions are common to all of them, so wiring the call sites
 * one by one would leave gaps — notably MFA users, who never reach
 * `establishSession`'s tail, and 401 expiry, which would otherwise leave the
 * previous user identified on a shared machine.
 */
export const mixpanelMiddleware: Middleware = () => (next) => (action: any) => {
  const result = next(action);

  // Matched off the creators, not string literals: renaming either reducer then
  // breaks the build instead of silently switching tracking off.
  if (action?.type === loginAction.type) {
    const payload = action.payload ?? {};
    // The password-expiry path dispatches a partial payload with no user
    // fields; identifyMixpanel no-ops when it cannot resolve a distinct id.
    identifyMixpanel({
      id: payload.userId,
      email: payload.email,
      account: payload.account,
      name: payload.userName,
    });
  }

  if (action?.type === logOutAction.type) {
    resetMixpanel();
  }

  return result;
};
